import { useMemo, useState } from "react";

import { ChevronDownIcon } from "@/components/icons/chevrons";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { fullLocationDisplayName } from "@/helpers/games";
import { cn } from "@/lib/utils";
import { GameStatusEnum } from "@/pages/game/types";

import type { CompanyType, LocationType } from "./types";

interface LocationFilterTypes {
  company: CompanyType;
  filteredLocation: LocationType | null;
  onClearFilter: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  onHandleFilter: (location: LocationType) => void;
  showClosedRooms: boolean;
}

interface LocationFilterItemProps {
  location: LocationType;
  onClick(location: LocationType): void;
  showClosedLocations: boolean;
}

function LocationFilterItem({
  location,
  onClick,
  showClosedLocations,
}: LocationFilterItemProps) {
  const handleClick = () => onClick(location);

  let games = location.games;

  if (!showClosedLocations) {
    games = games.filter((game) => game.status === GameStatusEnum.OP);

    if (!games.length) {
      return null;
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: its ok
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
    <li
      className="border-bright-pink px-3 py-1.5 text-black transition-[colors,border] hover:border-l-4 hover:bg-pale-pink hover:text-bright-pink"
      onClick={handleClick}
    >
      {fullLocationDisplayName(location)} ({games.length})
    </li>
  );
}

export default function LocationFilter({
  company,
  filteredLocation,
  onClearFilter,
  onHandleFilter,
  showClosedRooms,
}: LocationFilterTypes) {
  const [isOpen, setIsOpen] = useState(false);

  const handleFilter = (location: LocationType) => {
    setIsOpen(false);
    onHandleFilter(location);
  };

  const selectedLabel = useMemo(() => {
    if (filteredLocation) {
      return fullLocationDisplayName(filteredLocation);
    }

    let locations = company.locations;

    if (!showClosedRooms) {
      locations = locations.filter((location) =>
        location.games.some((game) => game.status === GameStatusEnum.OP)
      );
    }

    return `All Locations (${locations.length})`;
  }, [company.locations, showClosedRooms, filteredLocation]);

  return (
    <div className="flex flex-col items-center justify-start md:flex-row md:justify-between">
      <h2 className="mt-17.5 mb-10 font-semibold text-32 md:text-48">
        Locations
      </h2>
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        <div className="relative">
          <PopoverTrigger
            className={cn(
              "h-[55px] w-full rounded-[20px] bg-white px-5 font-medium text-black"
            )}
          >
            {selectedLabel}
            {!!filteredLocation && <ChevronDownIcon />}
          </PopoverTrigger>
          {filteredLocation && (
            <Button
              className="absolute top-[5px] right-0 text-24 text-bright-pink"
              onClick={onClearFilter}
              size="icon"
            >
              x
            </Button>
          )}
        </div>
        <PopoverContent
          align="end"
          asChild
          className="max-h-50 w-auto min-w-80 overflow-y-auto rounded-[20px] px-0 py-2"
        >
          <ul>
            {company.locations.map((location: LocationType) => (
              <LocationFilterItem
                key={location.id}
                location={location}
                onClick={handleFilter}
                showClosedLocations={showClosedRooms}
              />
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}
